import React from 'react';
import { Box, styled } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationsAllocationsTable, {
  ColumnConfig,
} from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { AllocationRule } from 'further-types/lp/allocations';
import { round } from 'lodash';
import { useFormContext } from 'react-hook-form';
import NumericInputCell from 'components/EqualisationElements/NumericInputCell';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import useEqualiseRealisationAllocations from '../../hooks/useEqualiseRealisationAllocations';

type RealisationAllocation = {
  _id: string;
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  excludeFromAllocation?: boolean;
  unitsAllocated: number;
};

type EqualisationRealisationAllocationsFormProps = {
  fields?: Array<RealisationAllocation>;
  transactionCurrency?: CurrencyCode;
  allocationRule: AllocationRule;
  isNotEditable: boolean;
  actionIndex: number;
  allocationType:
    | 'originalRealisationAllocations'
    | 'newRealisationAllocations';
  linkedActionIndex?: number;
};

const StyledEmptyMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const EqualisationRealisationAllocationsForm: React.FC<
  EqualisationRealisationAllocationsFormProps
> = ({
  transactionCurrency = CurrencyCode.GBP,
  isNotEditable,
  actionIndex,
  allocationType,
  linkedActionIndex,
  allocationRule,
}) => {
  const { control } = useFormContext();

  const shouldRender = linkedActionIndex !== undefined;

  const {
    allocationsPath,
    watchedFields,
    liveTotalAllocationPercentage,
    liveTotalUnitsAllocated,
    liveTotalReceivedConsiderationLcy,
    liveTotalEscrowConsiderationLcy,
    liveTotalEarnoutConsiderationLcy,
    liveTotalWithholdingTaxPayableLcy,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    handleExcludeFromWithholdingTaxChange,
    handleAllocationRuleChange,
  } = useEqualiseRealisationAllocations({
    actionIndex,
    linkedActionIndex: linkedActionIndex ?? 0,
    allocationType,
  });

  if (!shouldRender) {
    return (
      <StyledEmptyMessage>
        No realisation allocations available
      </StyledEmptyMessage>
    );
  }

  if (!watchedFields || watchedFields.length === 0) {
    return (
      <StyledEmptyMessage>
        No realisation allocations available
      </StyledEmptyMessage>
    );
  }

  const columns: Array<ColumnConfig> = [
    {
      header: 'Investor',
      renderCell: (row) => row.investorLabel,
    },
    {
      header: 'Allocation (%)',
      renderCell: (_row, rowIndex) => (
        <NumericInputCell
          control={control}
          name={`${allocationsPath}.${rowIndex}.allocationPercentage`}
          onChange={(value) =>
            handleAllocationPercentageChange(value, rowIndex)
          }
          disabled={isNotEditable || _row.excludeFromAllocation}
        />
      ),
      renderFooter: () => `${round(liveTotalAllocationPercentage, 2)}%`,
    },
    {
      header: 'Exclude investor from allocation',
      renderCell: (_row, rowIndex) => (
        <CheckboxCell
          control={control}
          name={`${allocationsPath}.${rowIndex}.excludeFromAllocation`}
          onChange={(value) =>
            handleExcludeFromAllocationChange(value, rowIndex)
          }
          disabled={isNotEditable}
        />
      ),
    },
    {
      header: 'Units allocated',
      renderCell: (row) => row.unitsAllocated.toLocaleString(),
      renderFooter: () => liveTotalUnitsAllocated.toLocaleString(),
    },
    {
      header: 'Received consideration',
      renderCell: (row) =>
        numberToCurrencyString(row.receivedConsiderationLcy, {
          currency: transactionCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(liveTotalReceivedConsiderationLcy, {
          currency: transactionCurrency,
        }),
    },
    {
      header: 'Escrow consideration',
      renderCell: (row) =>
        numberToCurrencyString(row.escrowConsiderationLcy, {
          currency: transactionCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(liveTotalEscrowConsiderationLcy, {
          currency: transactionCurrency,
        }),
    },
    {
      header: 'Earnout consideration',
      renderCell: (row) =>
        numberToCurrencyString(row.earnoutConsiderationLcy, {
          currency: transactionCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(liveTotalEarnoutConsiderationLcy, {
          currency: transactionCurrency,
        }),
    },
    {
      header: 'Withholding tax',
      renderCell: (row) =>
        numberToCurrencyString(row.withholdingTaxPayableLcy, {
          currency: transactionCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(liveTotalWithholdingTaxPayableLcy, {
          currency: transactionCurrency,
        }),
    },
    {
      header: 'Exclude user from withholding tax',
      renderCell: (_row, rowIndex) => (
        <CheckboxCell
          control={control}
          name={`${allocationsPath}.${rowIndex}.withholdingTaxExempt`}
          onChange={(value) =>
            handleExcludeFromWithholdingTaxChange(value, rowIndex)
          }
          disabled={isNotEditable || _row.excludeFromAllocation}
        />
      ),
    },
  ];

  const rows = watchedFields.map((field) => ({
    ...field,
  }));

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      footerLabel={`${watchedFields?.length} investors`}
      isPending={false}
      showAllocationRule={true}
      isNotEditable={isNotEditable}
      onChangeAllocationRule={(value) => {
        handleAllocationRuleChange(value);
      }}
      allocationRule={allocationRule}
      allocationRuleOptions={[
        {
          value: AllocationRule.PerOriginalInvestment,
          label: 'Per original investment',
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
      ]}
    />
  );
};

export default EqualisationRealisationAllocationsForm;
